import React, { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { ArrowRight, ChevronLeft, ChevronRight, Heart, Star, ShoppingCart, BookOpen, Package, Truck, Library, Instagram } from "lucide-react";
import { useStore } from "@/context/StoreContext";
import { useCart } from "@/context/CartContext";
import StoreShell from "@/components/StoreShell";
import { Reveal } from "@/components/pharmacy/hooks";
import { ManagedReviews, ManagedInstagram } from "@/components/storefront/HomeSections";
import { BOOK, BookHeading, authorOf } from "@/components/books/BookParts";
import { hasPriceRange } from "@/lib/variants";
import { homeSets } from "@/lib/homesets";
import { inr, rating, reviews } from "@/lib/storeutils";

const pct = (p) => (p.compare_at_price ? Math.round((1 - p.price / p.compare_at_price) * 100) : 0);
const CAT_COLORS = ["#b91c1c", "#a8714f", "#e79ac7", "#d4b04c", "#7fb0bf", "#7c6bc4"];
const PASTELS = [["#fbe5e7", "#f4b9bf"], ["#f8ead9", "#ebc88f"], ["#e6f3f6", "#b9dde4"], ["#f8e6f6", "#ebbfe6"]];

export default function BookHome() {
  const s = useStore();
  const cart = useCart();
  const [params] = useSearchParams();
  const q = (params.get("q") || "").toLowerCase();
  useEffect(() => { if (q && !s.loading) document.getElementById("shop")?.scrollIntoView({ behavior: "smooth" }); }, [q, s.loading]);

  const all = s.products;
  const featured = useMemo(() => all.filter((p) => p.featured), [all]);
  if (s.loading || !s.store) return <div className="min-h-screen flex items-center justify-center"><span className="w-10 h-10 rounded-full border-4 border-slate-200 border-t-green-700 animate-spin" /></div>;
  if (s.error) return <div className="min-h-screen flex items-center justify-center text-slate-500">{s.error}</div>;

  const lead = featured.length ? featured : all;
  // three shelves with different products: new arrivals, featured picks, on sale / bestsellers (see lib/homesets.js)
  const sets = homeSets(all, { fresh: 6, featured: 6, sale: 6 });
  const withOff = [...all].filter((p) => p.compare_at_price).sort((a, b) => pct(b) - pct(a));
  const cats = s.categories;
  const catTiles = cats.map((name) => ({ name, items: all.filter((p) => p.category === name), image: all.find((p) => p.category === name && p.image)?.image }));
  const publishers = [...new Set(all.map((p) => p.brand).filter(Boolean))].slice(0, 6);
  const has = all.length > 0;
  const renderCard = (p) => <BookCard p={p} s={s} cart={cart} />;


  const shown = all.filter((p) => p.name.toLowerCase().includes(q) || authorOf(p).toLowerCase().includes(q));
  return (
    <StoreShell>
      {q ? (
        <section id="shop" className="max-w-6xl mx-auto px-5 py-10" data-testid="book-search-results">
          <BookHeading title={`Results for “${q}”`} to={`/store/${s.slug}/shop`} label="Open shop" />
          {shown.length === 0 ? <div className="text-center py-16 text-slate-500">No books found.</div> : (
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-5" data-testid="storefront-products">{shown.map((p) => <BookCard key={p.id} p={p} s={s} cart={cart} />)}</div>
          )}
        </section>
      ) : (
        <>
          <Hero s={s} slides={lead.slice(0, 3)} promo={lead[3] || lead[1] || lead[0]} />
          {sets.sale.length > 0 && <Shelf id="bestsellers" s={s} cart={cart} title="Shop bestsellers" items={sets.sale} />}
          {catTiles.length > 0 && <Categories s={s} tiles={catTiles} />}
          {has && <About s={s} items={all} />}
          {sets.fresh.length > 0 && <NewArrivals s={s} cart={cart} items={sets.fresh} />}
          {has && <PromoBand s={s} items={lead.slice(0, 8)} cat={cats[0]} />}
          {catTiles.length > 0 && <Collections s={s} tiles={catTiles.slice(0, 4)} />}
          {sets.featured.length > 0 && <Shelf id="picks" s={s} cart={cart} title="Picks for you" items={sets.featured} />}
          {has && <BannerPair s={s} items={(withOff.length >= 2 ? withOff : lead).slice(0, 2)} />}
          <ManagedReviews s={s} layout="books" fallback={<Testimonials s={s} />} />
          {publishers.length > 0 && <Publishers names={publishers} />}
          <ManagedInstagram s={s} layout="books" fallback={<InstaGrid s={s} />} />
        </>
      )}
    </StoreShell>
  );
}

/* ---------- decorative shapes of the hero ---------- */
function Decor() {
  return (
    <div aria-hidden className="pointer-events-none absolute inset-0 hidden md:block">
      <span className="absolute -left-10 -top-10 w-44 h-44 rounded-full" style={{ background: "repeating-linear-gradient(135deg,#f6d86a 0 3px,transparent 3px 9px)" }} />
      <span className="absolute left-4 top-[42%] w-14 h-14" style={{ backgroundImage: "radial-gradient(#1d3a8a 2px,transparent 2px)", backgroundSize: "12px 12px" }} />
      <span className="absolute left-12 top-[62%] w-9 h-9 rounded-full border-[6px] rx-float-slow" style={{ borderColor: "#f08a4b" }} />
      <span className="absolute left-5 bottom-8 w-14 h-14 rx-float" style={{ background: "#f6a62a", borderRadius: "4px 4px 0 0" }}><span className="absolute left-5 top-5 w-10 h-10 rounded-full" style={{ background: "#dc4a1d" }} /></span>
      <span className="absolute right-2 top-5 w-24 h-24 rx-float-slow" style={{ background: "#f6a62a", borderRadius: "0 0 0 4px" }}><span className="absolute right-0 top-[-14px] w-20 h-20 rounded-tl-full" style={{ background: "#6a9a3c" }} /><span className="absolute right-[-14px] top-[-18px] w-20 h-20 rounded-tl-full" style={{ background: "#7aa0e6" }} /></span>
      <span className="absolute right-0 top-[46%] w-8 h-8 rounded-full" style={{ background: "#e03b3b" }} />
      <span className="absolute right-6 top-[58%] w-7 h-7 rounded-full" style={{ background: "#f6cc4a" }} />
      <span className="absolute right-4 bottom-6 w-16 h-16 rounded-full" style={{ background: "repeating-linear-gradient(135deg,#ec9b7c 0 2px,transparent 2px 7px)" }} />
    </div>
  );
}

/* ---------- Hero: large gradient banner + promo card ---------- */
function Hero({ s, slides, promo }) {
  const { c, slug } = s;
  const [i, setI] = useState(0);
  const [paused, setPaused] = useState(false);
  const n = Math.max(slides.length, 1);
  useEffect(() => { if (paused || n < 2) return undefined; const t = setInterval(() => setI((x) => (x + 1) % n), 6000); return () => clearInterval(t); }, [paused, n]);
  const p = slides[i];
  const off = promo ? pct(promo) : 0;
  return (
    <section className="relative overflow-hidden" style={{ background: BOOK.cream }} data-testid="book-hero">
      <Decor />
      <div className="relative max-w-6xl mx-auto px-5 py-8 md:py-14 grid lg:grid-cols-[1.9fr_1fr] gap-5">
        <div className="relative rounded-2xl overflow-hidden min-h-[320px] md:min-h-[430px] flex items-center" style={{ background: "linear-gradient(120deg,#1c2f86 0%,#2a5bb5 55%,#3fa7e0 100%)" }}
          onMouseEnter={() => setPaused(true)} onMouseLeave={() => setPaused(false)}>
          <div key={i} className="relative z-10 px-7 md:px-10 py-10 max-w-[60%]">
            <div className="rx-slide-up text-[13px] md:text-[15px] font-semibold" style={{ color: "#ff5b5b" }}>{p?.category ? `${p.category} you should read.` : "Books you should read."}</div>
            <h1 className="rx-slide-up mt-3 text-[22px] sm:text-[32px] md:text-[40px] leading-[1.08] uppercase text-white line-clamp-4 break-words" style={{ fontFamily: BOOK.serif, fontWeight: 700, animationDelay: ".1s" }}>{p ? p.name : `Welcome to ${s.store.name}`}</h1>
            <Link to={p ? `/store/${slug}/product/${p.id}` : `/store/${slug}/shop`} data-testid="storefront-shop-collection" className="rx-slide-up rx-btn mt-7 inline-flex items-center gap-2 px-6 py-3 rounded-full bg-white text-[13px] font-semibold" style={{ color: BOOK.ink, animationDelay: ".2s" }}>Discover Now <ChevronRight size={15} /></Link>
          </div>
          {p?.image && <span key={`img${i}`} className="rx-slide-in absolute right-[7%] bottom-[8%] h-[78%] max-w-[34%]" style={{ aspectRatio: "2/3" }}><span className="bk3d bk3d-hero rx-float w-full h-full" style={{ transform: "perspective(1000px) rotateY(-14deg) rotateZ(-2deg)" }}><img src={p.image} alt={p.name} /></span></span>}
          {n > 1 && <>
            <button onClick={() => setI((i - 1 + n) % n)} aria-label="Previous slide" className="hidden sm:flex absolute left-3 top-1/2 -translate-y-1/2 w-9 h-9 rounded-full bg-white/90 flex items-center justify-center hover:scale-110 transition-transform"><ChevronLeft size={17} /></button>
            <button onClick={() => setI((i + 1) % n)} aria-label="Next slide" className="hidden sm:flex absolute right-3 top-1/2 -translate-y-1/2 w-9 h-9 rounded-full bg-white/90 flex items-center justify-center hover:scale-110 transition-transform"><ChevronRight size={17} /></button>
            <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex gap-2">{slides.map((_, k) => <button key={k} aria-label={`Slide ${k + 1}`} onClick={() => setI(k)} className="h-1.5 rounded-full transition-all duration-500" style={{ width: i === k ? 24 : 7, background: i === k ? "#fff" : "rgba(255,255,255,.5)" }} />)}</div>
          </>}
        </div>

        {promo && (
          <Link to={`/store/${slug}/product/${promo.id}`} className="rx-card relative rounded-2xl overflow-hidden flex flex-col items-center text-center px-6 pt-10 pb-8" style={{ background: "linear-gradient(180deg,#f9855d,#f9a07f)" }}>
            <div className="relative">
              {promo.image && <span className="bk3d bk3d-hero rx-float w-[150px] md:w-[170px]" style={{ aspectRatio: "2/3", transform: "perspective(900px) rotateY(-10deg)" }}><img src={promo.image} alt={promo.name} /></span>}
              <span className="absolute -top-4 -right-12 w-[74px] h-[74px] flex flex-col items-center justify-center font-extrabold text-neutral-900 rx-beat" style={{ background: "#fbe27a", clipPath: "polygon(50% 0%,61% 11%,75% 7%,80% 21%,94% 25%,90% 39%,100% 50%,90% 61%,94% 75%,80% 79%,75% 93%,61% 89%,50% 100%,39% 89%,25% 93%,20% 79%,6% 75%,10% 61%,0% 50%,10% 39%,6% 25%,20% 21%,25% 7%,39% 11%)" }}>
                {promo.compare_at_price ? <span className="text-[10px] line-through opacity-70 leading-none">{inr(promo.compare_at_price)}</span> : null}
                <span className="text-[15px] leading-tight">{inr(promo.price)}</span>
              </span>
            </div>
            <div className="mt-6 text-[13px] font-medium" style={{ color: "#3b2a8a" }}>{off > 0 ? `Save ${off}% today` : promo.category || "New in store"}</div>
            <h3 className="mt-1 text-[22px] md:text-[26px] leading-tight uppercase text-white line-clamp-2" style={{ fontFamily: BOOK.serif, fontWeight: 700 }}>{promo.name}</h3>
            <span className="rx-btn mt-5 inline-flex items-center gap-2 px-6 py-2.5 rounded-full bg-white text-[13px] font-semibold" style={{ color: BOOK.ink }}>Shop Now <ChevronRight size={14} /></span>
          </Link>
        )}
      </div>
    </section>
  );
}

/* ---------- Product shelf (6 across) ---------- */
function Shelf({ id, s, cart, title, items }) {
  return (
    <section id={id} className="max-w-6xl mx-auto px-5 py-10">
      <BookHeading title={title} to={`/store/${s.slug}/shop`} />
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-5" data-testid={id === "bestsellers" ? "storefront-products" : undefined}>
        {items.map((p, k) => <Reveal key={p.id} delay={(k % 6) * 70} kind="rv-zoom"><BookCard p={p} s={s} cart={cart} /></Reveal>)}
      </div>
    </section>
  );
}

/* ---------- Shop by category ---------- */
function Categories({ s, tiles }) {
  const { slug } = s;
  return (
    <section id="categories" className="max-w-6xl mx-auto px-5 py-10">
      <BookHeading title="Shop by category" />
      <div className="flex gap-5 overflow-x-auto pb-3 snap-x" style={{ scrollbarWidth: "none" }}>
        {tiles.map((t, k) => (
          <Reveal key={t.name} delay={k * 80} kind="rv-zoom" className="snap-start shrink-0 w-[250px] sm:w-[262px]">
            <Link to={`/store/${slug}/shop?category=${encodeURIComponent(t.name)}`} className="rx-card block rounded-lg border bg-white overflow-hidden h-full" style={{ borderColor: BOOK.line }} data-testid={`storefront-cat-tile-${t.name}`}>
              <div className="relative h-[58px] flex items-center justify-end pr-4 text-white text-[15px] font-semibold" style={{ background: CAT_COLORS[k % CAT_COLORS.length], marginTop: 0 }}>
                {t.image && <span className="bk3d absolute left-4 -top-3 h-[104px] w-[70px]"><img src={t.image} alt="" /></span>}
                <span className="max-w-[55%] text-right leading-tight line-clamp-2">{t.name}</span>
              </div>
              <ul className="pl-[96px] pr-4 pt-6 pb-5 space-y-2 min-h-[120px]">
                {t.items.slice(0, 4).map((p) => <li key={p.id} className="text-[12px] line-clamp-1" style={{ color: "#6b665e" }}>{p.name}</li>)}
                <li className="text-[11px] font-semibold inline-flex items-center gap-1" style={{ color: CAT_COLORS[k % CAT_COLORS.length] }}>{t.items.length} title{t.items.length === 1 ? "" : "s"} <ArrowRight size={11} /></li>
              </ul>
            </Link>
          </Reveal>
        ))}
      </div>
    </section>
  );
}

/* ---------- About ---------- */
function About({ s, items }) {
  const { c, slug, store } = s;
  const covers = items.filter((p) => p.image).slice(0, 5);
  const rot = [-24, 18, -8, 28, -14];
  const pos = [["6%", "4%"], ["38%", "0%"], ["64%", "26%"], ["2%", "46%"], ["34%", "56%"]];
  const feats = [[BookOpen, "Selection", "A carefully chosen catalogue of titles and stationery across every genre and budget."], [Package, "Quality packaging", "Every order is checked and wrapped securely so it reaches you in pristine condition."], [Truck, "Free shipping & more", "Qualifying orders ship to your door free, with live tracking from checkout to delivery."]];
  return (
    <section id="about" className="relative overflow-hidden mt-8" style={{ background: "#f6f4f0" }}>
      <div className="max-w-6xl mx-auto px-5 grid lg:grid-cols-2 gap-8 items-center">
        <div className="relative h-[340px] md:h-[460px] order-2 lg:order-1">
          {covers.map((p, k) => <span key={p.id} className="bk3d rx-float-slow absolute w-[120px] md:w-[150px]" style={{ left: pos[k][0], top: pos[k][1], transform: `rotate(${rot[k]}deg)`, aspectRatio: "2/3", animationDelay: `${k * .6}s` }}><img src={p.image} alt={p.name} /></span>)}
        </div>
        <div className="py-12 order-1 lg:order-2">
          <Reveal><div className="text-[11px] font-semibold tracking-[.2em] uppercase" style={{ color: "#8a847a" }}>About {store.name}</div>
            <h2 className="mt-3 text-[30px] md:text-[36px] leading-tight" style={{ fontFamily: BOOK.serif, fontWeight: 400, color: BOOK.ink }}>{store.tagline || `The online bookshop for curious readers`}</h2></Reveal>
          <div className="mt-6 space-y-5">
            {feats.map(([I, h, d], k) => (
              <Reveal key={h} delay={k * 100} className="flex gap-4">
                <span className="relative shrink-0 w-12 h-12 flex items-center justify-center" style={{ color: BOOK.ink }}><span className="absolute right-0 top-0 w-5 h-5 rounded-full" style={{ background: "#e5bf80" }} /><I size={30} strokeWidth={1.3} className="relative" /></span>
                <div><div className="text-[17px]" style={{ fontFamily: BOOK.serif }}>{h}</div><p className="text-[12.5px] leading-relaxed mt-0.5" style={{ color: "#6b665e" }}>{d}</p></div>
              </Reveal>
            ))}
          </div>
          <Reveal delay={300}><Link to={`/store/${slug}/about`} className="rx-btn mt-7 inline-flex items-center gap-2 px-6 py-3 rounded-full bg-white text-[13px] font-semibold" style={{ color: BOOK.ink }}>More About Us <ChevronRight size={14} /></Link></Reveal>
        </div>
      </div>
    </section>
  );
}

/* ---------- New arrivals: cover + details rows ---------- */
function NewArrivals({ s, cart, items }) {
  return (
    <section id="new-arrivals" className="max-w-6xl mx-auto px-5 py-12">
      <BookHeading title="New arrivals" to={`/store/${s.slug}/shop`} />
      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-x-8 gap-y-7">
        {items.map((p, k) => <Reveal key={p.id} delay={(k % 3) * 80} className="pb-6 border-b" style={{ borderColor: BOOK.line }}><BookCard p={p} s={s} cart={cart} row /></Reveal>)}
      </div>
    </section>
  );
}

/* ---------- Dark promo band with tilted covers ---------- */
function PromoBand({ s, items, cat }) {
  const { slug } = s;
  const covers = items.filter((p) => p.image).slice(0, 8);
  const lead = items[0];
  if (!lead) return null;
  return (
    <section className="max-w-6xl mx-auto px-5 py-6">
      <Reveal kind="rv-zoom">
        <div className="relative overflow-hidden rounded-2xl min-h-[300px] flex items-center" style={{ background: "linear-gradient(110deg,#17322f 0%,#1d5a52 55%,#17322f 100%)" }}>
          <div className="relative z-10 px-8 md:px-14 py-10 max-w-[52%]">
            <div className="text-[12px] font-semibold" style={{ color: "#f3d34a" }}>{cat ? `${cat} collection.` : "Best collection."}</div>
            <h2 className="mt-2 text-[26px] md:text-[38px] leading-tight uppercase text-white" style={{ fontFamily: BOOK.serif, fontWeight: 700 }}>Top favourite {cat ? cat : "reads"}</h2>
            <p className="mt-3 text-[13px] text-white/80">Find our take on the best books of all time.</p>
            <Link to={cat ? `/store/${slug}/shop?category=${encodeURIComponent(cat)}` : `/store/${slug}/shop`} className="rx-btn mt-6 inline-flex items-center gap-2 px-6 py-3 rounded-full bg-white text-[13px] font-semibold" style={{ color: BOOK.ink }}>Discover Now <ChevronRight size={14} /></Link>
          </div>
          <div aria-hidden className="absolute right-0 top-0 h-full w-[52%] grid grid-cols-4 gap-3 p-4 content-center" style={{ transform: "rotate(-14deg) scale(1.15)", transformOrigin: "center" }}>
            {covers.map((p, k) => <span key={p.id} className="bk3d w-full" style={{ aspectRatio: "2/3", marginTop: k % 2 ? 22 : 0 }}><img src={p.image} alt="" /></span>)}
          </div>
        </div>
      </Reveal>
    </section>
  );
}

/* ---------- Featured collections (pastel cards) ---------- */
function Collections({ s, tiles }) {
  return (
    <section className="max-w-6xl mx-auto px-5 py-10">
      <BookHeading title="Featured collections" />
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-5">
        {tiles.map((t, k) => {
          const [bg, dot] = PASTELS[k % PASTELS.length];
          const covers = t.items.filter((p) => p.image).slice(0, 3);
          return (
            <Reveal key={t.name} delay={k * 90} kind="rv-zoom">
              <Link to={`/store/${s.slug}/shop?category=${encodeURIComponent(t.name)}`} className="rx-card block rounded-lg overflow-hidden text-center pb-5" style={{ background: bg }}>
                <div className="relative h-[190px] flex items-center justify-center">
                  <span className="absolute w-[120px] h-[120px] rounded-full" style={{ background: dot }} />
                  <div className="relative">
                    {covers.map((p, i) => <span key={p.id} className="bk3d absolute" style={{ width: 84, aspectRatio: "2/3", left: "50%", top: "50%", transform: `translate(-50%,-50%) translate(${(i - 1) * 9}px,${(2 - i) * 7}px) rotate(${(i - 1) * 7}deg)`, zIndex: i }}><img src={p.image} alt="" /></span>)}
                    <span className="block" style={{ width: 90, height: 130 }} />
                  </div>
                </div>
                <div className="text-[18px] px-3" style={{ fontFamily: BOOK.serif, color: BOOK.ink }}>{t.name}</div>
                <span className="mt-1 inline-flex items-center gap-1 text-[12px] font-semibold underline" style={{ color: BOOK.ink }}>Shop All <ChevronRight size={12} /></span>
              </Link>
            </Reveal>
          );
        })}
      </div>
    </section>
  );
}

/* ---------- Two promo banners ---------- */
function BannerPair({ s, items }) {
  const { slug } = s;
  const themes = [["linear-gradient(120deg,#b4541a,#e07b19 60%,#c96a14)", "#f3d34a", "#e11d2e"], ["linear-gradient(120deg,#4b2a8e,#7b46b7 60%,#4b2a8e)", "#f0a6c4", "#e11d2e"]];
  return (
    <section className="max-w-6xl mx-auto px-5 py-8 grid md:grid-cols-2 gap-5">
      {items.map((p, k) => p && (
        <Reveal key={p.id} kind={k ? "rv-right" : "rv-left"}>
          <Link to={`/store/${slug}/product/${p.id}`} className="rx-zoom relative flex items-center justify-between h-[200px] md:h-[210px] rounded-2xl overflow-hidden px-6 md:px-8 text-white" style={{ background: themes[k % 2][0] }}>
            <div className="relative shrink-0">
              {p.image && <span className="bk3d h-[150px]" style={{ aspectRatio: "2/3", transform: "perspective(800px) rotateY(-12deg) rotateZ(-3deg)" }}><img src={p.image} alt={p.name} /></span>}
              <span className="absolute -top-3 -right-8 w-[62px] h-[62px] flex flex-col items-center justify-center text-[10px] font-extrabold text-neutral-900 rx-beat" style={{ background: themes[k % 2][1], clipPath: "polygon(50% 0%,61% 11%,75% 7%,80% 21%,94% 25%,90% 39%,100% 50%,90% 61%,94% 75%,80% 79%,75% 93%,61% 89%,50% 100%,39% 89%,25% 93%,20% 79%,6% 75%,10% 61%,0% 50%,10% 39%,6% 25%,20% 21%,25% 7%,39% 11%)" }}>
                {p.compare_at_price ? <span className="line-through opacity-70 leading-none">{inr(p.compare_at_price)}</span> : null}<span className="text-[13px]">{inr(p.price)}</span>
              </span>
            </div>
            <div className="flex-1 pl-12 min-w-0">
              <div className="text-[12px] font-medium" style={{ color: themes[k % 2][1] }}>{p.category || "Featured"}</div>
              <h3 className="mt-1 text-[22px] md:text-[26px] leading-tight uppercase line-clamp-3" style={{ fontFamily: BOOK.serif, fontWeight: 700 }}>{p.name}</h3>
              <div className="mt-4 flex items-center gap-3 flex-wrap text-[12px]"><span>Buy online at <u>{s.store.name}</u></span><span className="rx-btn inline-flex items-center gap-1.5 px-4 py-2 rounded-full bg-white text-[12px] font-semibold" style={{ color: BOOK.ink }}>Shop Now <ChevronRight size={13} /></span></div>
            </div>
          </Link>
        </Reveal>
      ))}
    </section>
  );
}

/* ---------- Testimonials ---------- */
const SAMPLE_REVIEWS = [
  ["Joanne H.", "This is my favourite bookshop", "Love that you give books to those in need. Great service and I can find great value here, especially as I prefer hardcovers."],
  ["Viorel M.", "Quick and easy", "Prices are low, there are always promotions or discounts, and the website is easy to navigate."],
  ["Ciaren R.", "Excellent service", "The books were wrapped securely and arrived in pristine condition. I emailed after and got a prompt reply."],
  ["Margaret C.", "Best bookshop online", "I am so happy to have found a shop for unusual titles. The whole experience was phenomenal."],
];
function Testimonials({ s }) {
  const { c } = s;
  return (
    <section id="reviews" className="max-w-6xl mx-auto px-5 py-12">
      <BookHeading title="What our readers say" />
      <div className="flex gap-5 items-start overflow-x-auto pb-3" style={{ scrollbarWidth: "none" }}>
        <Reveal className="shrink-0 flex flex-col items-center">
          <div className="w-[150px] h-[150px] rounded-full flex flex-col items-center justify-center text-white" style={{ background: c.primary }}>
            <div className="text-[34px] font-bold leading-none" style={{ color: "#e9c27d" }}>4.8<span className="text-[13px]">/5</span></div>
            <div className="flex gap-0.5 mt-1.5">{[...Array(5)].map((_, i) => <Star key={i} size={10} fill="#e9c27d" color="#e9c27d" />)}</div>
            <div className="text-[10px] mt-1 underline">Happy readers</div>
          </div>
          <Link to={`/store/${s.slug}/contact`} className="rx-btn -mt-4 inline-flex items-center gap-1.5 px-4 py-2 rounded-full border bg-white text-[12px] font-semibold" style={{ borderColor: BOOK.line }}>Write Your Review <ChevronRight size={12} /></Link>
        </Reveal>
        {SAMPLE_REVIEWS.map(([n, t, d], k) => (
          <Reveal key={n} delay={k * 90} className="shrink-0 w-[300px] rounded-lg border bg-white p-6" style={{ borderColor: BOOK.line }}>
            <div className="text-[12px] font-semibold">{n}</div>
            <div className="flex gap-0.5 mt-1.5 pb-3 border-b" style={{ borderColor: BOOK.line }}>{[...Array(5)].map((_, i) => <Star key={i} size={11} fill={c.primary} color={c.primary} />)}</div>
            <div className="mt-4 text-[14px] font-semibold">{t}</div>
            <p className="mt-2 text-[12.5px] leading-relaxed" style={{ color: "#6b665e" }}>{d}</p>
          </Reveal>
        ))}
      </div>
    </section>
  );
}

function Publishers({ names }) {
  return (
    <section className="max-w-6xl mx-auto px-5 py-10" data-testid="book-publishers">
      <Reveal className="flex items-center gap-5"><h2 className="text-[30px] whitespace-nowrap" style={{ fontFamily: BOOK.serif, fontWeight: 400 }}>Featured publishers</h2><span className="flex-1 h-px" style={{ background: BOOK.line }} /></Reveal>
      <div className="mt-8 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-6 items-center">
        {names.map((n, k) => <Reveal key={n} delay={k * 70} className="flex items-center justify-center gap-1.5 text-[17px] font-bold text-center" style={{ fontFamily: BOOK.serif, color: BOOK.ink }}><Library size={18} className="shrink-0" /><span className="line-clamp-2">{n}</span></Reveal>)}
      </div>
    </section>
  );
}

/* ---------- Instagram strip (default homepage; vendors who use Homepage Management get their own posts instead) ---------- */
function InstaGrid({ s }) {
  const imgs = s.products.filter((p) => p.image).slice(0, 6);
  if (imgs.length === 0) return null;
  return (
    <section className="max-w-6xl mx-auto px-5 py-10" data-testid="book-instagram">
      <BookHeading title={<>Follow <b>@{s.store.name.replace(/\s+/g, "").toUpperCase()}</b> on Instagram</>} />
      <div className="grid grid-cols-3 md:grid-cols-6 gap-3">
        {imgs.map((p, k) => (
          <Reveal key={p.id} kind="rv-zoom" delay={k * 70}>
            <Link to={`/store/${s.slug}/product/${p.id}`} aria-label={p.name} className="group relative block aspect-square overflow-hidden rounded-lg">
              <img src={p.image} alt={p.name} className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110" />
              <span className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity text-white" style={{ background: "rgba(15,61,39,.6)" }}><Instagram size={22} /></span>
            </Link>
          </Reveal>
        ))}
      </div>
    </section>
  );
}

/* ---------- Product card ---------- */
export function BookCard({ p, s, cart, row }) {
  const { c, slug } = s;
  const off = pct(p);
  const hasVariants = p.variants?.length > 0 || p.variant_items?.length > 0;
  const soldOut = p.stock <= 0;
  const [added, setAdded] = useState(false);
  const author = authorOf(p, s.store.name);
  const add = (e) => {
    e.preventDefault();
    if (hasVariants) { window.location.href = `/store/${slug}/product/${p.id}`; return; }
    cart.add(p, null, 1); toast.success("Added to cart"); setAdded(true); setTimeout(() => setAdded(false), 1200);
  };
  const stars = (
    <span className="inline-flex items-center gap-px">{[...Array(5)].map((_, i) => <Star key={i} size={9} fill={i < Math.round(rating(p.id)) ? "#e9a23b" : "#ddd"} color={i < Math.round(rating(p.id)) ? "#e9a23b" : "#ddd"} />)}<span className="text-[10px] ml-1" style={{ color: "#8a847a" }}>({reviews(p.id)})</span></span>
  );
  const cover = (
    <Link to={`/store/${slug}/product/${p.id}`} className={`relative block overflow-hidden rounded-lg ${row ? "w-[130px] shrink-0" : ""}`} style={{ aspectRatio: "2/3", background: "#efece6" }}>
      {p.image ? <img src={p.image} alt={p.name} className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105" /> : <BookOpen size={34} className="m-auto mt-[40%] text-slate-300" />}
      {off > 0 && <span className="absolute top-2 left-2 w-9 h-9 rounded-full flex items-center justify-center text-[10px] font-bold text-white" style={{ background: c.accent }}>-{off}%</span>}
      {soldOut && <span className="absolute top-2 left-2 px-2 py-0.5 rounded text-[10px] font-semibold text-white" style={{ background: "#6b6bb0" }}>Sold Out</span>}
      <span className="absolute top-2 right-2 w-7 h-7 rounded-full bg-white shadow flex items-center justify-center hover:text-red-500" aria-hidden><Heart size={13} /></span>
      {!row && <button type="button" onClick={add} disabled={soldOut} data-testid={`storefront-add-to-cart-btn-${p.id}`} className="absolute inset-x-2 bottom-2 py-2 rounded-full bg-white text-[11px] font-bold flex items-center justify-center gap-1.5 translate-y-12 group-hover:translate-y-0 transition-transform duration-300 disabled:opacity-60" style={{ color: added ? c.primary : BOOK.ink }}>
        <ShoppingCart size={12} /> {added ? "Added" : hasVariants ? "Select options" : soldOut ? "Sold out" : "Add to cart"}
      </button>}
    </Link>
  );
  return (
    <div data-testid={`storefront-product-${p.id}`} className={`group ${row ? "flex gap-4" : "text-center"}`}>
      {cover}
      <div className={row ? "min-w-0 flex-1" : "mt-3"}>
        <div className={row ? "" : "-mt-1"}>{stars}</div>
        <Link to={`/store/${slug}/product/${p.id}`}><h3 className={`mt-1 leading-snug line-clamp-2 transition-colors group-hover:text-[color:var(--bk)] ${row ? "text-[15px]" : "text-[13.5px]"}`} style={{ fontFamily: BOOK.serif, color: BOOK.ink, "--bk": c.primary }}>{p.name}</h3></Link>
        <div className="mt-1 text-[10.5px] underline line-clamp-1" style={{ color: "#8a847a" }}>{author}</div>
        <div className={`mt-1.5 flex items-baseline gap-1.5 ${row ? "" : "justify-center"}`}>
          {hasPriceRange(p) && <span className="text-[10px]" style={{ color: "#8a847a" }}>From</span>}
          <span className="text-[14px] font-bold" style={{ color: c.primary }}>{inr(p.price)}</span>
          {p.compare_at_price ? <span className="text-[11px] line-through" style={{ color: "#9a948a" }}>{inr(p.compare_at_price)}</span> : null}
        </div>
        {row && <button type="button" onClick={add} disabled={soldOut} data-testid={`storefront-add-to-cart-btn-${p.id}`} className="mt-3 inline-flex items-center gap-1.5 px-4 py-1.5 rounded-full border text-[11px] font-semibold disabled:opacity-50" style={{ borderColor: BOOK.line, color: BOOK.ink }}>
          <ShoppingCart size={11} /> {added ? "Added" : hasVariants ? "Select options" : soldOut ? "Sold out" : "Add to cart"}</button>}
      </div>
    </div>
  );
}
